import { useState, useEffect, useMemo } from "react";
import { query, where, getCountFromServer, getDocs } from "firebase/firestore";
import {
  dbTasks, dbNotes, dbLiftLogs, dbWorkoutSessions,
  dbCommunityPosts, dbDailyLogs, dbLists, dbBodyWeightLogs,
  dbCardioLogs, dbReferrals,
} from "../firebase";
import { useUser } from "../contexts/UserContext";
import { useTheme } from "../contexts/ThemeSelection";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { BadgeData, BadgeStatus } from "../types/badges";
import { evaluateBadges } from "../data/badges";

const DEFAULT_NAV = ["notes", "tasks", "calendar"];

/** Compute longest consecutive-day streak from sorted YYYY-MM-DD date strings. */
function computeStreaks(dates: string[]): { longest: number; current: number } {
  if (dates.length === 0) return { longest: 0, current: 0 };

  const sorted = Array.from(new Set(dates)).sort();
  let longest = 1;
  let current = 1;
  let streak = 1;

  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1] + "T00:00:00");
    const curr = new Date(sorted[i] + "T00:00:00");
    const diffDays = Math.round((curr.getTime() - prev.getTime()) / 86400000);
    if (diffDays === 1) {
      streak++;
      if (streak > longest) longest = streak;
    } else {
      streak = 1;
    }
  }

  // Current streak: count backwards from the last date
  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  const lastDate = sorted[sorted.length - 1];

  if (lastDate === today || lastDate === yesterday) {
    current = 1;
    for (let i = sorted.length - 2; i >= 0; i--) {
      const prev = new Date(sorted[i] + "T00:00:00");
      const curr = new Date(sorted[i + 1] + "T00:00:00");
      const diffDays = Math.round((curr.getTime() - prev.getTime()) / 86400000);
      if (diffDays === 1) {
        current++;
      } else {
        break;
      }
    }
  } else {
    current = 0;
  }

  return { longest, current };
}

type UseBadgesReturn = {
  badges: BadgeStatus[];
  loading: boolean;
  unlockedCount: number;
  totalCount: number;
};

const useBadges = (): UseBadgesReturn => {
  const user = useUser();
  const { activeTheme } = useTheme();
  const { settings } = useUserSettings();
  const uid = user?.userDetails?.user?.uid;

  const [counts, setCounts] = useState<{
    tasks: number;
    tasksCompleted: number;
    notes: number;
    lifts: number;
    sessions: number;
    posts: number;
    dailyLogs: number;
    lists: number;
    bodyWeightEntries: number;
    cardioEntries: number;
    referrals: number;
    totalVolumeLbs: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) return;

    let cancelled = false;

    const fetchCounts = async () => {
      try {
        const [
          tasksSnap,
          tasksCompletedSnap,
          notesSnap,
          liftsSnap,
          sessionsSnap,
          postsSnap,
          dailyLogsSnap,
          listsSnap,
          bodyWeightSnap,
          cardioSnap,
          referralsSnap,
        ] = await Promise.all([
          getCountFromServer(query(dbTasks, where("userId", "==", uid))),
          getCountFromServer(query(dbTasks, where("userId", "==", uid), where("toggle", "==", true))),
          getCountFromServer(query(dbNotes, where("userId", "==", uid))),
          getCountFromServer(query(dbLiftLogs, where("userId", "==", uid))),
          getCountFromServer(query(dbWorkoutSessions, where("userId", "==", uid), where("status", "==", "completed"))),
          getCountFromServer(query(dbCommunityPosts, where("userId", "==", uid))),
          getCountFromServer(query(dbDailyLogs, where("userId", "==", uid))),
          getCountFromServer(query(dbLists, where("userId", "==", uid))),
          getCountFromServer(query(dbBodyWeightLogs, where("userId", "==", uid))),
          getCountFromServer(query(dbCardioLogs, where("userId", "==", uid))),
          getCountFromServer(query(dbReferrals, where("referrerId", "==", uid))),
        ]);

        // Fetch lift volume (need actual data for this)
        let totalVolumeLbs = 0;
        try {
          const liftDocs = await getDocs(query(dbLiftLogs, where("userId", "==", uid)));
          liftDocs.forEach((doc) => {
            const d = doc.data();
            totalVolumeLbs += (d.weight ?? 0) * (d.sets ?? 1) * (d.reps ?? 1);
          });
        } catch {
          // If too many docs, skip volume calculation
        }

        if (!cancelled) {
          setCounts({
            tasks: tasksSnap.data().count,
            tasksCompleted: tasksCompletedSnap.data().count,
            notes: notesSnap.data().count,
            lifts: liftsSnap.data().count,
            sessions: sessionsSnap.data().count,
            posts: postsSnap.data().count,
            dailyLogs: dailyLogsSnap.data().count,
            lists: listsSnap.data().count,
            bodyWeightEntries: bodyWeightSnap.data().count,
            cardioEntries: cardioSnap.data().count,
            referrals: referralsSnap.data().count,
            totalVolumeLbs,
          });
          setLoading(false);
        }
      } catch (err) {
        console.error("Badge data fetch error:", err);
        if (!cancelled) setLoading(false);
      }
    };

    fetchCounts();
    return () => { cancelled = true; };
  }, [uid]);

  const badges = useMemo(() => {
    if (!user?.userDetails || !counts) return evaluateBadges(emptyData());

    const details = user.userDetails;
    const loginRewards = details.loginRewards;
    const claimedDates = loginRewards?.claimedDates ?? [];
    const { longest, current } = computeStreaks(claimedDates);

    // Days since account creation
    const dateCreated = details.dateCreated;
    const daysOnApp = dateCreated
      ? Math.floor((Date.now() - new Date(dateCreated).getTime()) / 86400000)
      : 0;

    // Check if nav has been customised
    const navItems = settings.navBarItems ?? DEFAULT_NAV;
    const hasCustomNav = JSON.stringify(navItems) !== JSON.stringify(DEFAULT_NAV);

    // Check if theme has been changed from default
    const hasCustomTheme = activeTheme !== "pink";

    const data: BadgeData = {
      loginCount: claimedDates.length,
      longestLoginStreak: longest,
      currentLoginStreak: current,
      taskCount: counts.tasks,
      tasksCompleted: counts.tasksCompleted,
      noteCount: counts.notes,
      workoutSessions: counts.sessions,
      liftEntries: counts.lifts,
      totalVolumeKg: counts.totalVolumeLbs * 0.453592,
      communityPosts: counts.posts,
      familyMembers: details.familyMembers?.length ?? 0,
      hasPremium: !!details.subscription?.active,
      hasCustomTheme,
      hasCustomNav,
      isEarlyAdopter: !!details.earlyAdopter,
      isBetaTester: !!details.isBetaTester,
      hasReferral: counts.referrals > 0,
      daysOnApp,
      dailyLogCount: counts.dailyLogs,
      listCount: counts.lists,
      bodyWeightEntries: counts.bodyWeightEntries,
      cardioEntries: counts.cardioEntries,
    };

    return evaluateBadges(data);
  }, [user?.userDetails, counts, settings.navBarItems, activeTheme]);

  const unlockedCount = badges.filter((b) => b.unlocked).length;

  return { badges, loading, unlockedCount, totalCount: badges.length };
};

function emptyData(): BadgeData {
  return {
    loginCount: 0, longestLoginStreak: 0, currentLoginStreak: 0,
    taskCount: 0, tasksCompleted: 0, noteCount: 0, workoutSessions: 0,
    liftEntries: 0, totalVolumeKg: 0, communityPosts: 0, familyMembers: 0,
    hasPremium: false, hasCustomTheme: false, hasCustomNav: false,
    isEarlyAdopter: false, isBetaTester: false, hasReferral: false, daysOnApp: 0,
    dailyLogCount: 0, listCount: 0, bodyWeightEntries: 0, cardioEntries: 0,
  };
}

export default useBadges;
